import { inject, makeEnvironmentProviders } from '@angular/core';

import { Store } from '@ngrx/store';
import { firstValueFrom, map } from 'rxjs';

import { GuaranteeApplicationsService } from '@hm/core/services/guarantee-applications.service';
import { MemberProfilesService } from '@hm/core/services/member-profiles.service';
import { selectUser } from '@hm/core/stores/hm/hm.selectors';

import { environment } from '@hm/environments/environment';

import { provideChatState } from '@lib/chat/core/providers/chat-state.provider';
import { ChatCommunicationService } from '@lib/chat/core/services/chat-communication.service';
import { CHAT_API_URL, CHAT_LIB_CONFIG } from '@lib/chat/core/tokens/chat.tokens';
import { ChatLibConfig, ThreadData } from '@lib/chat/models/chat-config.interface';

import { ActionResponseBody } from '@lib/core/models/action.interface';
import { GuaranteeApplication } from '@lib/core/models/guarantee-application.interface';
import { MemberProfile } from '@lib/core/models/member.interface';

export function provideChatLibConfig() {
  return makeEnvironmentProviders([
    { provide: CHAT_API_URL, useValue: environment.healthManagerApiUrl },
    {
      provide: CHAT_LIB_CONFIG,
      useFactory: (): ChatLibConfig => {
        const memberProfilesService = inject(MemberProfilesService);
        const chatApiService = inject(ChatCommunicationService);
        const guaranteesService = inject(GuaranteeApplicationsService);
        const store = inject(Store);
        const hmProfile = store.selectSignal(selectUser);

        return {
          showMedicalHistory: true,
          hasInternalProfile: true,
          ownUserInfo: {
            userId: hmProfile().userId,
            senderName: hmProfile().fullName,
          },
          notificationConfig: {
            title: 'У вас нове повідомлення',
            icon: 'dist/assets/images/logo.svg',
            soundEnabled: true,
          },
          resolveThreadData: async (threadId: string) => {
            try {
              const thread = await firstValueFrom(chatApiService.getThread(threadId));

              let memberProfileInfo: MemberProfile | null = null;
              let memberGuarantees: GuaranteeApplication[] = [];
              let memberComments: ActionResponseBody[] = [];

              try {
                memberProfileInfo = await firstValueFrom(memberProfilesService.getClientProfileByUserId(thread.ownerId));

                if (memberProfileInfo) {
                  const [guarantees, comments] = await Promise.all([
                    firstValueFrom(guaranteesService.searchGuaranteeApplications({ userId: thread.ownerId }).pipe(map(res => res.value))),
                    firstValueFrom(memberProfilesService.getClientProfileComments(memberProfileInfo.id).pipe(map(res => res.value))),
                  ]);
                  memberGuarantees = guarantees;
                  memberComments = comments;
                }
              } catch (memberError) {
                console.warn('Could not load member data, continuing with thread only:', memberError);
              }

              return { thread, memberProfileInfo, memberGuarantees, memberComments } as ThreadData;
            } catch (error) {
              console.error('Error resolving thread data:', error);
              throw error;
            }
          },
        };
      },
      deps: [],
    },
    provideChatState(),
  ]);
}
